"use client"
import { useGSAP } from "@gsap/react";
import clsx from "clsx";
import gsap from "gsap";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import AnimatedLink from "@/components/animated-link";

import OverlayWrapper from '@/components/overlay-wrapper/overlay-wrapper'
import { ASSET_PREFIX, STORIES_BASE_PATH } from "@/constants/routes";
import { useApp } from "@/context/AppContext";
import { categories } from "@/data/static-data";

export default function RecordsNavOverlay({ className = "" }) {
  const cats = categories
  const titles = useRef([])
  const titlesLines = useRef([])
  const {isRecordsNavOverlayOpen, setIsRecordsNavOverlayOpen} = useApp()
  const pathname = usePathname()
  const [activeLink, setActiveLink] = useState(null)

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isRecordsNavOverlayOpen) {
        e.stopPropagation();
        setIsRecordsNavOverlayOpen(false)
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isRecordsNavOverlayOpen]);

  useEffect(() => {
    const headerEl = document.documentElement.querySelector('#HeaderRegion');
    const footerEl = document.documentElement.querySelector('#FooterRegion');
    const main = document.documentElement.querySelector('.stories-of-us-app-main');

    if (isRecordsNavOverlayOpen) {
      headerEl.setAttribute('inert', true)
      footerEl.setAttribute('inert', true)
      main.setAttribute('inert', true)
    }

    return () => {
      headerEl.removeAttribute('inert')
      footerEl.removeAttribute('inert')
      main.removeAttribute('inert', true)
    }


  }, [isRecordsNavOverlayOpen])

  const animateIn = () => {
    gsap.killTweensOf([titles.current]);

    gsap.to(titles.current, {
      y: 0,
      duration: 1.2,
      ease: 'expo.out',
      delay: 0.4,
      stagger: 0.1,
    })

    gsap.to(titlesLines.current, {
      scaleX: 1,
      duration: 1.2,
      ease: 'expo.out',
      delay: 0.4,
      stagger: 0.1,
    })
  }

  const animateOutBase = (dir) => {
    gsap.killTweensOf([titles.current]);

    gsap.to(titles.current, {
      y: dir * 100,
      duration: 1.2,
      delay: 0.1,
      ease: 'expo.out',
      stagger: 0.1,
    })

    gsap.to(titlesLines.current, {
      scaleX: 0,
      duration: 1.2,
      delay: 0.1,
      ease: 'expo.out',
      stagger: 0.1,
    })
  }

  const animateOut = () => {
    animateOutBase(-1)
  }

  const animateOutBack = () => {
    animateOutBase(1)
  }

  const [currentHoverIndex, setCurrentHoverIndex] = useState(-1)

  const handleLinkIn = (index) => {
    setCurrentHoverIndex(index)
  }

  const handleLinkOut = () => {
    setCurrentHoverIndex(-1)
  }

  useEffect(() => {
    if (isRecordsNavOverlayOpen, pathname) {
      setActiveLink(pathname)
    }
  }, [isRecordsNavOverlayOpen, pathname])

  useGSAP(() => {
    if (isRecordsNavOverlayOpen && titles.current?.length > 0) {
      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: reduce)", () => {
        gsap.set(titles.current, {
          opacity: 1,
          y: 0
        })
  
        gsap.set(titlesLines.current, {
          scaleX: 1,
          transformOrigin: 'left center'
        })
  
      });
  
      mm.add("(prefers-reduced-motion: no-preference)", () => {
        gsap.set(titles.current, {
          // opacity: 0,
          y: 100
        })
  
        gsap.set(titlesLines.current, {
          scaleX: 0,
          transformOrigin: 'left center'
        })

        animateIn()
      });
    }
  }, [isRecordsNavOverlayOpen, titles])

  const [isLinkClicked, setIsLinkClicked] = useState(false);

  return (
    <OverlayWrapper isLinkClicked={isLinkClicked} setIsLinkClicked={setIsLinkClicked} isOpen={isRecordsNavOverlayOpen} setIsOpen={setIsRecordsNavOverlayOpen}>
      <div className="justify-center fixed top-0 left-0 bg-main-dark w-full h-full relative">
        <img alt="" src={`${ASSET_PREFIX}/img/svg/topography-bg.svg`} className="absolute top-0 left-0 w-full h-full object-cover pointer-events-none" />
        <div className="flex justify-center items-center w-full h-full px-6 lg:px-20">
          <ul className="flex flex-col text-main-light w-full">
            {cats.map((cat, index) => {
              const isLast = index === cats.length - 1;
              const href= `${STORIES_BASE_PATH}/record-gallery${cat.slug === 'featured' ? '' : '/' + cat.slug}`;
              return (
                <li
                  key={cat.slug}
                  className={clsx(
                    "pb-(--space-5) lg:pb-(--space-6) xl:pb-(--space-5) relative overflow-y-clip t-opacity opacity-50",
                    index < cats.length - 1 && 'mb-(--space-5) lg:mb-(--space-5) mb-(--space-5)',
                    ((href === activeLink) || (currentHoverIndex === index)) && 'opacity-100'
                  )}
                >
                  <AnimatedLink
                    href={href}
                    onClick={() => setIsLinkClicked(true)}
                    onPointerEnter={() => handleLinkIn(index)}
                    onPointerLeave={handleLinkOut}
                    onFocus={() => handleLinkIn(index)}
                    onBlur={handleLinkOut}
                    className={'block -outline-offset-2'}
                  >
                    <h2 className="relative -top-0. 5 subnav-smaller md:subnav-small lg:heading-category lg:-top-0.75 will-change-transform whitespace-nowrap" ref={(el) => titles.current[index] = el}>{cat.title}</h2>
                    <div className={clsx("absolute bottom-0 left-0 w-full h-[1px] border-b border-dashed  border-current/20 will-change-transform", isLast && "max-lg:hidden")} ref={(el) => titlesLines.current[index] = el} />
                  </AnimatedLink>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </OverlayWrapper>
  )
}
